import * as THREE from 'three/webgpu';
import { attribute, float, mix, normalLocal, positionGeometry, positionViewDirection, sin, smoothstep, step, texture, transformNormalToView, uniform, vec3, vec4 } from 'three/tsl';
import { BossState, type GameView } from '../../shared/types';
import type { GameClocks } from '../clock';
import { phaseOf } from '../util';
import { loadAnimatedActor, type ActorAsset } from './gltf';
import { bossLookMaterial, EMPTY_ATTACH, loadAttachments, REGION_LOOKS, type RegionLook } from './regionAttach';
import { MOB_CLIPS, PoseBlend } from './animation';
import { skinNodes } from './skinning';
import { EMBER_HEIGHT, emberCasting, emberFrame } from './emberAnimation';
import { toonGradient } from './toon';

/** 전용 보스 모델(20261018-r2-enemies). rest 몸 키(m, 지팡이 끝 제외)로 EMBER_HEIGHT를 맞춘다. */
export const EMBER_BOSS_ACTOR = 'boss_ember_archmage';
const EMBER_BOSS_REST = 1.9;

export class EmberBossVisual {
  readonly root = new THREE.Group();
  private asset: ActorAsset | null = null;
  private material: THREE.Material | null = null;
  private readonly pose = uniform(new THREE.Vector3());
  private readonly glow = uniform(0.2);
  /** D3a 변형 색조(서리 리치·독안개 사제)와 크기 */
  private readonly tint = uniform(new THREE.Color(1, 1, 1));
  /** c021·C2 지역 변형: 지팡이 구슬 발광 색(잿불 주황 → 서리 청백·독 연두·모래 청록·심연 보라), 조각 발광 값, 조각 붙은 메시(같은 재질) */
  private readonly orb = uniform(new THREE.Color(1.6, 0.12, 0.005));
  private readonly attGain = uniform(1.5);
  private readonly attPulse = uniform(0);
  private readonly sparkle = uniform(new THREE.Color(0, 0, 0));
  private readonly time = uniform(0);
  private base: THREE.Mesh | null = null;
  private readonly lookMesh: Partial<Record<RegionLook, THREE.Mesh>> = {};
  private look: RegionLook | null = null;
  private legacy = false;
  /** QA 전후 비교(BossVisual.legacyLook): 켜면 원형 메시 */
  setLegacy(on: boolean): void { if (on !== this.legacy) { this.legacy = on; this.applyLook(); } }
  setVariant(tint: number, scale: number, look: RegionLook | null = null): void {
    (this.tint.value as THREE.Color).setHex(tint); this.root.scale.setScalar(scale);
    this.look = look;
    const lm = bossLookMaterial(look);
    (this.orb.value as THREE.Color).setRGB(lm.orb[0], lm.orb[1], lm.orb[2]);
    this.attGain.value = lm.gain;
    this.attPulse.value = lm.pulse;
    (this.sparkle.value as THREE.Color).setRGB(lm.sparkle[0], lm.sparkle[1], lm.sparkle[2]);
    this.applyLook();
  }
  private applyLook(): void {
    const lm = this.look && !this.legacy ? this.lookMesh[this.look] : undefined;
    if (this.base) this.base.visible = !lm;
    for (let i = 0; i < REGION_LOOKS.length; i++) { const m = this.lookMesh[REGION_LOOKS[i]]; if (m) m.visible = m === lm; }
  }
  lookReady(look: RegionLook): boolean { return !!this.lookMesh[look]; }
  /** 지역 변형(서리 리치·독안개 사제) 메시. 전용 잿불 모델일 때만, 원형 재질·골격 텍스처를 그대로 쓴다 */
  async loadLook(look: RegionLook): Promise<void> {
    if (this.lookMesh[look] || !this.dedicated || !this.material || !this.asset?.skin) return;
    if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('r34legacy')) return;
    const parts = await loadAttachments(EMBER_BOSS_ACTOR, look);
    if (!parts || this.lookMesh[look]) return;
    const a = await loadAnimatedActor(EMBER_BOSS_ACTOR, MOB_CLIPS, parts);
    if (!a?.skin || !a.geometry.hasAttribute('aAtt') || this.lookMesh[look]) { a?.geometry.dispose(); a?.skin?.texture.dispose(); return; }
    a.skin.texture.dispose();
    const mesh = new THREE.Mesh(a.geometry, this.material);
    mesh.castShadow = true; mesh.frustumCulled = false; mesh.scale.setScalar(this.unit); mesh.visible = false;
    this.root.add(mesh);
    this.lookMesh[look] = mesh;
    this.applyLook();
  }
  private readonly blend = new PoseBlend(1);
  private cycle = 0;
  private unit = 1;
  /** 전용 모델을 쓰는지(QA·대체 확인) */
  dedicated = false;
  async load(): Promise<void> {
    const legacy = typeof location !== 'undefined' && new URLSearchParams(location.search).has('r2legacy');
    // 빈 조각 묶음: 원형에도 aAtt(0)를 달아 지역 변형 메시와 같은 재질을 쓴다
    let a = legacy ? null : await loadAnimatedActor(EMBER_BOSS_ACTOR, MOB_CLIPS, EMPTY_ATTACH);
    if (a?.skin) this.dedicated = true;
    else {
      // 전용 모델이 없거나 깨지면 기존 버섯 주술사 확대판으로 대신한다
      a?.geometry.dispose();
      a = await loadAnimatedActor('mob_mushroom_shaman', MOB_CLIPS);
    }
    this.asset = a;
    if (!a) return;
    const sk = a.skin ? skinNodes(a.skin, this.pose, positionGeometry, normalLocal, attribute('aSmooth', 'vec3')) : null;
    const m = new THREE.MeshToonNodeMaterial({ gradientMap: toonGradient() });
    m.positionNode = sk ? sk.position : positionGeometry;
    const normal = transformNormalToView(sk ? sk.normal : normalLocal);
    m.normalNode = normal;
    const tex = a.map ? texture(a.map).rgb : vec3(0.5, 0.2, 0.12);
    const att = this.dedicated && a.skin ? attribute('aAtt', 'vec4') : vec4(0);
    const attMask = step(0.5, att.w);
    m.colorNode = mix((this.dedicated ? tex : tex.mul(vec3(0.94, 0.82, 0.74))).mul(this.tint), att.xyz, attMask);
    const rest = a.skin ? attribute('aActorRest', 'vec3') : positionGeometry;
    // 발광: 버섯판은 갓 전체, 전용 모델은 오른손 지팡이 끝 용암 구슬만(모델 공간 rest 기준). 몸 전체가 주황으로 뜨지 않게 가장자리 빛도 줄인다.
    const cap = this.dedicated
      ? smoothstep(a.height * 0.84, a.height * 0.92, rest.y).mul(smoothstep(0.35, 0.5, rest.x.negate()))
      : smoothstep(a.height * 0.65, a.height * 0.85, rest.y);
    const fres = float(1).sub(normal.dot(positionViewDirection).clamp(0, 1)).pow(3);
    const attGlow = att.w.sub(1).max(0).mul(attMask);
    const pulse = float(1).sub(this.attPulse.mul(0.5)).add(sin(this.time.mul(0.06).add(rest.y.mul(9))).mul(this.attPulse.mul(0.5)));
    (m as unknown as { emissiveNode: unknown }).emissiveNode = this.orb.mul(cap.mul(float(1).sub(attMask)).mul(this.glow).add(fres.mul(this.dedicated ? 0.06 : 0.18)))
      // 지역 조각: 정점색 발광(시전 중 더 밝게) + 가장자리 반짝임
      .add(att.xyz.mul(attGlow.mul(this.attGain)).mul(pulse).mul(this.glow.mul(0.5).add(0.9)))
      .add(this.sparkle.mul(fres.mul(attMask).mul(0.55)));
    const mesh = new THREE.Mesh(a.geometry, m); mesh.castShadow = true; mesh.frustumCulled = false; this.base = mesh;
    this.unit = EMBER_HEIGHT / (this.dedicated ? EMBER_BOSS_REST : a.height); mesh.scale.setScalar(this.unit); this.root.add(mesh); this.material = m;
  }
  reset(): void { this.blend.reset(); this.cycle = 0; }
  update(view: GameView, alpha: number, clocks: GameClocks): void {
    const b = view.boss, a = this.asset;
    this.root.position.set(b.prevX + (b.x - b.prevX) * alpha, 0, b.prevZ + (b.z - b.prevZ) * alpha);
    this.root.rotation.y = b.facing;
    if (!a?.skin) return;
    const speed = clocks.worldRate > 0 ? Math.hypot(b.x - b.prevX, b.z - b.prevZ) * 60 / clocks.worldRate : 0;
    const moving = speed > 0.02;
    const playback = b.state === BossState.Chase && moving ? speed / Math.max(0.1, a.skin.clips.run.speed * this.unit) : 1;
    this.cycle += clocks.worldDelta / 60 * playback;
    const frame = emberFrame(a.skin, b.state, phaseOf(b.stateTick, clocks.worldRate, alpha), this.cycle, moving);
    this.blend.update(0, b.state, frame, clocks.worldNow, b.state === BossState.Slam || b.state === BossState.Sweep || b.state === BossState.Dead);
    this.pose.value.set(this.blend.a[0], this.blend.b[0], this.blend.mix[0]);
    this.glow.value = emberCasting(b.state) ? 0.85 : b.state === BossState.Stagger ? 0.05 : 0.2;
    this.time.value = clocks.worldNow;
  }
  dispose(): void { for (const k of REGION_LOOKS) this.lookMesh[k]?.geometry.dispose(); this.asset?.geometry.dispose(); this.asset?.skin?.texture.dispose(); this.material?.dispose(); }
}
